import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { adminAPI } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Webhook, Key, Plus, Trash2, PlayCircle, RefreshCw, 
  CheckCircle2, XCircle, Clock, Copy, Eye, EyeOff,
  ArrowRight, ExternalLink, FileJson, Settings2, Zap
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const BACKEND_URL = '';

export default function Integrations() {
  const [activeTab, setActiveTab] = useState('webhooks');
  
  return (
    <Layout>
      <div className="space-y-6" data-testid="integrations-page">
        {/* Header with stats */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Zap className="h-6 w-6 text-primary" />
              </div>
              Integration Hub
            </h1>
            <p className="text-muted-foreground mt-2">
              Connect your portal with external systems via webhooks and API
            </p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 lg:w-[500px]">
            <TabsTrigger value="webhooks" className="gap-2" data-testid="webhooks-tab">
              <Webhook className="h-4 w-4" />
              <span className="hidden sm:inline">Webhooks</span>
            </TabsTrigger>
            <TabsTrigger value="api-keys" className="gap-2" data-testid="api-keys-tab">
              <Key className="h-4 w-4" />
              <span className="hidden sm:inline">API Keys</span>
            </TabsTrigger>
            <TabsTrigger value="docs" className="gap-2" data-testid="docs-tab">
              <FileJson className="h-4 w-4" />
              <span className="hidden sm:inline">Documentation</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="webhooks" className="mt-6">
            <WebhooksTab />
          </TabsContent>

          <TabsContent value="api-keys" className="mt-6">
            <ApiKeysTab />
          </TabsContent>

          <TabsContent value="docs" className="mt-6">
            <DocumentationTab />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

function WebhooksTab() {
  const [webhooks, setWebhooks] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [showLogsDialog, setShowLogsDialog] = useState(false);
  const [logs, setLogs] = useState([]);
  const [testing, setTesting] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [webhooksRes, eventsRes] = await Promise.all([
        adminAPI.getWebhooks(),
        adminAPI.getWebhookEvents()
      ]);
      setWebhooks(webhooksRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      toast.error('Failed to load webhooks');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this webhook?')) return;
    try {
      await adminAPI.deleteWebhook(id);
      toast.success('Webhook deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete webhook');
    }
  };

  const handleTest = async (webhook) => {
    setTesting(webhook.id);
    try {
      const res = await adminAPI.testWebhook(webhook.id);
      if (res.data.success) {
        toast.success(`Test successful! Status: ${res.data.status_code}`);
      } else {
        toast.error(`Test failed: ${res.data.error || `Status ${res.data.status_code}`}`);
      }
    } catch (error) {
      toast.error('Test failed');
    } finally {
      setTesting(null);
    }
  };

  const handleViewLogs = async (webhook) => {
    setSelectedWebhook(webhook);
    try {
      const res = await adminAPI.getWebhookLogs(webhook.id);
      setLogs(res.data);
      setShowLogsDialog(true);
    } catch (error) {
      toast.error('Failed to load logs');
    }
  };

  const handleToggleActive = async (webhook) => {
    try {
      await adminAPI.updateWebhook(webhook.id, { is_active: !webhook.is_active });
      toast.success(webhook.is_active ? 'Webhook disabled' : 'Webhook enabled');
      loadData();
    } catch (error) {
      toast.error('Failed to update webhook');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><RefreshCw className="h-8 w-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Outgoing Webhooks</h2>
          <p className="text-sm text-muted-foreground">Send events to external systems when things happen in your portal</p>
        </div>
        <CreateWebhookDialog 
          events={events} 
          open={showCreateDialog} 
          onOpenChange={setShowCreateDialog}
          onCreated={loadData}
        />
      </div>

      {webhooks.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Webhook className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No webhooks configured</h3>
            <p className="text-muted-foreground text-center mb-4">
              Set up webhooks to notify external systems when events occur
            </p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create First Webhook
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {webhooks.map((webhook) => (
            <Card key={webhook.id} className={!webhook.is_active ? 'opacity-60' : ''}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="space-y-2 flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold truncate">{webhook.name}</h3>
                      <Badge variant={webhook.is_active ? 'default' : 'secondary'}>
                        {webhook.is_active ? 'Active' : 'Disabled'}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground font-mono truncate">{webhook.url}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {webhook.events.slice(0, 4).map((event) => (
                        <Badge key={event} variant="outline" className="text-xs">{event}</Badge>
                      ))}
                      {webhook.events.length > 4 && (
                        <Badge variant="outline" className="text-xs">+{webhook.events.length - 4} more</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground mt-2">
                      <span className="flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3 text-green-500" />
                        {webhook.success_count} delivered
                      </span>
                      <span className="flex items-center gap-1">
                        <XCircle className="h-3 w-3 text-red-500" />
                        {webhook.failure_count} failed
                      </span>
                      {webhook.last_triggered_at && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Last: {formatDistanceToNow(new Date(webhook.last_triggered_at), { addSuffix: true })}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleTest(webhook)}
                      disabled={testing === webhook.id}
                    >
                      {testing === webhook.id ? (
                        <RefreshCw className="h-4 w-4 animate-spin" />
                      ) : (
                        <PlayCircle className="h-4 w-4" />
                      )}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleViewLogs(webhook)}>
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleToggleActive(webhook)}
                    >
                      <Settings2 className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDelete(webhook.id)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Logs Dialog */}
      <Dialog open={showLogsDialog} onOpenChange={setShowLogsDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Webhook Delivery Logs</DialogTitle>
            <DialogDescription>{selectedWebhook?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {logs.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No delivery logs yet</p>
            ) : (
              logs.map((log) => (
                <div 
                  key={log.id} 
                  className={`p-3 rounded-lg border ${log.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {log.success ? (
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-600" />
                      )}
                      <Badge variant="outline">{log.event}</Badge>
                      {log.status_code && <Badge variant="secondary">{log.status_code}</Badge>}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                    </span>
                  </div>
                  {log.error && <p className="text-sm text-red-600">{log.error}</p>}
                  {log.duration_ms && <p className="text-xs text-muted-foreground">Duration: {log.duration_ms}ms</p>}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CreateWebhookDialog({ events, open, onOpenChange, onCreated }) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !url || selectedEvents.length === 0) {
      toast.error('Please fill all required fields');
      return;
    }

    setSaving(true);
    try {
      await adminAPI.createWebhook({
        name,
        url,
        secret: secret || undefined,
        events: selectedEvents
      });
      toast.success('Webhook created');
      onOpenChange(false);
      setName('');
      setUrl('');
      setSecret('');
      setSelectedEvents([]);
      onCreated();
    } catch (error) {
      toast.error('Failed to create webhook');
    } finally {
      setSaving(false);
    }
  };

  const toggleEvent = (event) => {
    setSelectedEvents(prev => 
      prev.includes(event) 
        ? prev.filter(e => e !== event)
        : [...prev, event]
    );
  };

  const selectAllEvents = () => {
    setSelectedEvents(events.map(e => e.event));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Add Webhook
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Webhook</DialogTitle>
          <DialogDescription>Configure a webhook to send events to an external URL</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Name *</Label>
            <Input 
              id="name" 
              value={name} 
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Perfex CRM Sync"
            />
          </div>
          <div>
            <Label htmlFor="url">Endpoint URL *</Label>
            <Input 
              id="url" 
              value={url} 
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://your-system.com/webhook"
            />
          </div>
          <div>
            <Label htmlFor="secret">Secret (optional)</Label>
            <Input 
              id="secret" 
              value={secret} 
              onChange={(e) => setSecret(e.target.value)}
              placeholder="Used for HMAC signature verification"
            />
            <p className="text-xs text-muted-foreground mt-1">
              We'll sign payloads with this secret so you can verify they came from us
            </p>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Events *</Label>
              <Button type="button" variant="link" size="sm" onClick={selectAllEvents}>
                Select All
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[300px] overflow-y-auto border rounded-lg p-3">
              {events.map((event) => (
                <div 
                  key={event.event}
                  className="flex items-start space-x-2 p-2 hover:bg-muted rounded"
                >
                  <Checkbox
                    id={event.event}
                    checked={selectedEvents.includes(event.event)}
                    onCheckedChange={() => toggleEvent(event.event)}
                  />
                  <div className="space-y-0.5">
                    <label htmlFor={event.event} className="text-sm font-medium cursor-pointer">
                      {event.event}
                    </label>
                    <p className="text-xs text-muted-foreground">{event.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>
              {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
              Create Webhook
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ApiKeysTab() {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [showKey, setShowKey] = useState({});

  useEffect(() => {
    loadKeys();
  }, []);

  const loadKeys = async () => {
    try {
      const res = await adminAPI.getApiKeys();
      setApiKeys(res.data);
    } catch (error) {
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (name, permissions) => {
    try {
      const res = await adminAPI.createApiKey({ name, permissions });
      setNewKey(res.data);
      loadKeys();
      return true;
    } catch (error) {
      toast.error('Failed to create API key');
      return false;
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Are you sure? This will permanently disable this API key.')) return;
    try {
      await adminAPI.revokeApiKey(id);
      toast.success('API key revoked');
      loadKeys();
    } catch (error) {
      toast.error('Failed to revoke API key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><RefreshCw className="h-8 w-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Sync API Keys</h2>
          <p className="text-sm text-muted-foreground">Allow external systems to push data to your portal</p>
        </div>
        <CreateApiKeyDialog 
          open={showCreateDialog} 
          onOpenChange={setShowCreateDialog}
          onCreate={handleCreate}
        />
      </div>

      {/* New Key Display */}
      {newKey && (
        <Card className="border-green-500 bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-green-800">API Key Created!</h3>
                <p className="text-sm text-green-700 mb-2">
                  Copy this key now - it won't be shown again
                </p>
                <code className="bg-white px-3 py-2 rounded border text-sm block break-all">
                  {newKey.api_key}
                </code>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => copyToClipboard(newKey.api_key)}>
                  <Copy className="h-4 w-4 mr-1" /> Copy
                </Button>
                <Button size="sm" variant="outline" onClick={() => setNewKey(null)}>
                  Dismiss
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {apiKeys.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Key className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No API keys created</h3>
            <p className="text-muted-foreground text-center mb-4">
              Create API keys to allow external systems to sync data with your portal
            </p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create First API Key
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {apiKeys.map((key) => (
            <Card key={key.id} className={!key.is_active ? 'opacity-60' : ''}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{key.name}</h3>
                      <Badge variant={key.is_active ? 'default' : 'destructive'}>
                        {key.is_active ? 'Active' : 'Revoked'}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground font-mono">{key.key_prefix}...</p>
                    <div className="flex flex-wrap gap-1">
                      {key.permissions.map((perm) => (
                        <Badge key={perm} variant="outline" className="text-xs">{perm}</Badge>
                      ))}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>Created: {new Date(key.created_at).toLocaleDateString()}</span>
                      <span>Used: {key.usage_count} times</span>
                      {key.last_used_at && (
                        <span>Last: {formatDistanceToNow(new Date(key.last_used_at), { addSuffix: true })}</span>
                      )}
                    </div>
                  </div>
                  {key.is_active && (
                    <Button variant="outline" size="sm" onClick={() => handleRevoke(key.id)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateApiKeyDialog({ open, onOpenChange, onCreate }) {
  const [name, setName] = useState('');
  const [permissions, setPermissions] = useState(['clients', 'orders', 'services', 'payments']);
  const [saving, setSaving] = useState(false);

  const availablePermissions = [
    { id: 'clients', label: 'Clients', description: 'Create and update client records' },
    { id: 'orders', label: 'Orders', description: 'Create orders' },
    { id: 'services', label: 'Services', description: 'Sync client services/subscriptions' },
    { id: 'payments', label: 'Payments', description: 'Record payments' },
  ];

  const togglePermission = (perm) => {
    setPermissions(prev => 
      prev.includes(perm) 
        ? prev.filter(p => p !== perm)
        : [...prev, perm]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || permissions.length === 0) {
      toast.error('Please fill all required fields');
      return;
    }

    setSaving(true);
    const success = await onCreate(name, permissions);
    setSaving(false);
    
    if (success) {
      onOpenChange(false);
      setName('');
      setPermissions(['clients', 'orders', 'services', 'payments']);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Create API Key
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create API Key</DialogTitle>
          <DialogDescription>Generate a key for external systems to sync data</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="keyName">Name *</Label>
            <Input 
              id="keyName" 
              value={name} 
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Perfex CRM Integration"
            />
          </div>
          <div>
            <Label>Permissions *</Label>
            <div className="space-y-2 mt-2">
              {availablePermissions.map((perm) => (
                <div key={perm.id} className="flex items-start space-x-2 p-2 hover:bg-muted rounded">
                  <Checkbox
                    id={perm.id}
                    checked={permissions.includes(perm.id)}
                    onCheckedChange={() => togglePermission(perm.id)}
                  />
                  <div className="space-y-0.5">
                    <label htmlFor={perm.id} className="text-sm font-medium cursor-pointer">
                      {perm.label}
                    </label>
                    <p className="text-xs text-muted-foreground">{perm.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>
              {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
              Generate Key
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DocumentationTab() {
  const [copied, setCopied] = useState('');
  
  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(''), 2000);
  };

  const syncEndpoints = [
    {
      method: 'POST',
      path: '/api/sync/clients',
      description: 'Create or update a client',
      body: `{
  "email": "client@example.com",      // Required
  "full_name": "John Doe",            // Optional
  "company_name": "Acme Inc",         // Optional
  "phone": "+30 123 456 7890",        // Optional
  "vat_number": "EL123456789",        // Optional
  "tags": ["vip", "agency"],          // Optional
  "external_id": "CRM-12345"          // Optional - your system's ID
}`
    },
    {
      method: 'POST',
      path: '/api/sync/orders',
      description: 'Create an order',
      body: `{
  "client_email": "client@example.com",  // Required (or client_id)
  "items": [
    { "name": "WordPress Hosting", "price": 99.00, "quantity": 1 }
  ],
  "total": 122.76,                       // Including VAT
  "status": "completed",                 // Optional: pending, completed
  "external_id": "INV-001"               // Optional
}`
    },
    {
      method: 'POST',
      path: '/api/sync/services',
      description: 'Sync a client service/subscription',
      body: `{
  "client_email": "client@example.com",  // Required (or client_id)
  "service_name": "WordPress Hosting",   // Required
  "price_paid": 99.00,                   // Required
  "end_date": "2025-12-31",              // Required
  "status": "active",                    // Optional: active, expired
  "auto_renew": true,                    // Optional
  "external_id": "SUB-001"               // Optional
}`
    },
    {
      method: 'POST',
      path: '/api/sync/payments',
      description: 'Record a payment',
      body: `{
  "client_email": "client@example.com",  // Required (or client_id)
  "amount": 122.76,                      // Required
  "order_id": "order-uuid",              // Optional - links to an order
  "payment_method": "bank_transfer",     // Optional
  "external_id": "PAY-001"               // Optional
}`
    }
  ];

  return (
    <div className="space-y-8">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ArrowRight className="h-5 w-5" />
            Incoming Sync API
          </CardTitle>
          <CardDescription>
            Push data from your external systems (CRM, billing, etc.) to this portal
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="p-4 bg-muted rounded-lg">
            <h4 className="font-semibold mb-2">Authentication</h4>
            <p className="text-sm text-muted-foreground mb-2">
              All sync endpoints require an API key in the <code className="bg-background px-1 rounded">X-API-Key</code> header
            </p>
            <pre className="bg-background p-3 rounded text-sm overflow-x-auto">
{`curl -X POST ${BACKEND_URL}/api/sync/clients \\
  -H "X-API-Key: your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"email": "client@example.com"}'`}
            </pre>
          </div>

          {syncEndpoints.map((endpoint, i) => (
            <div key={i} className="border rounded-lg overflow-hidden">
              <div className="flex items-center justify-between p-3 bg-muted">
                <div className="flex items-center gap-2">
                  <Badge variant={endpoint.method === 'POST' ? 'default' : 'secondary'}>
                    {endpoint.method}
                  </Badge>
                  <code className="text-sm font-mono">{endpoint.path}</code>
                </div>
                <span className="text-sm text-muted-foreground">{endpoint.description}</span>
              </div>
              <div className="p-3 relative">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="absolute top-2 right-2"
                  onClick={() => copyToClipboard(endpoint.body, `endpoint-${i}`)}
                >
                  {copied === `endpoint-${i}` ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                </Button>
                <pre className="text-sm overflow-x-auto pr-12">{endpoint.body}</pre>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Webhook className="h-5 w-5" />
            Outgoing Webhooks
          </CardTitle>
          <CardDescription>
            Receive events when things happen in the portal
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-muted rounded-lg">
            <h4 className="font-semibold mb-2">Webhook Payload Format</h4>
            <pre className="bg-background p-3 rounded text-sm overflow-x-auto">
{`{
  "event": "order.paid",
  "timestamp": "2025-01-23T10:30:00Z",
  "data": {
    "order_id": "abc-123",
    "client_id": "client-456",
    "total": 122.76,
    ...
  }
}`}
            </pre>
          </div>

          <div className="p-4 bg-muted rounded-lg">
            <h4 className="font-semibold mb-2">Signature Verification</h4>
            <p className="text-sm text-muted-foreground mb-2">
              If you provide a secret, we sign payloads with HMAC-SHA256. Verify using:
            </p>
            <pre className="bg-background p-3 rounded text-sm overflow-x-auto">
{`# Header: X-Webhook-Signature: sha256=<signature>

import hmac
import hashlib

expected = hmac.new(
    secret.encode(),
    payload_body.encode(),
    hashlib.sha256
).hexdigest()

if f"sha256={expected}" == signature_header:
    # Valid webhook`}
            </pre>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ExternalLink className="h-5 w-5" />
            Integration Examples
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 border rounded-lg">
              <h4 className="font-semibold mb-2">Perfex CRM</h4>
              <p className="text-sm text-muted-foreground">
                Use webhooks to sync clients and orders. Configure a webhook receiver in Perfex modules.
              </p>
            </div>
            <div className="p-4 border rounded-lg">
              <h4 className="font-semibold mb-2">Zapier / Make</h4>
              <p className="text-sm text-muted-foreground">
                Use our webhook events to trigger Zaps or Scenarios for any automation.
              </p>
            </div>
            <div className="p-4 border rounded-lg">
              <h4 className="font-semibold mb-2">Custom Integration</h4>
              <p className="text-sm text-muted-foreground">
                Use the Sync API to push data from any system that can make HTTP requests.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
